import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bookingAPI } from '../services/api';
import { 
  ArrowLeft, User, Phone, Calendar, 
  Loader2, Plus, Minus, Home, CreditCard, Banknote, Save,
  MessageCircle, CalendarPlus, AlertCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const BookingEditScreen = () => {
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const { selectedProperty } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [booking, setBooking] = useState(null);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    guest_name: '',
    mobile_number: '',
    rooms: [{ room_name: '', room_count: 1 }],
    payment_mode: 'Cash',
    advance_amount: '',
    total_amount: '',
    check_in_date: '',
    check_out_date: '',
    booked_on_date: '',
    send_whatsapp: false,
    whatsapp_number: '',
  });

  // Fetch booking data
  useEffect(() => {
    const fetchBooking = async () => {
      if (!bookingId) return;
      
      setLoading(true);
      try {
        const response = await bookingAPI.getBooking(bookingId);
        if (response.STATUS === 1 && response.RESULT) {
          const b = response.RESULT;
          setBooking(b);
          setFormData({
            guest_name: b.guest_name || '',
            mobile_number: b.mobile_number || '',
            rooms: b.rooms?.length > 0 ? b.rooms : [{ room_name: '', room_count: 1 }],
            payment_mode: b.payment_mode || 'Cash',
            advance_amount: b.advance_amount?.toString() || '0',
            total_amount: (b.total_amount || b.total_price || b.a_property_gross_charges || b.payment_amount)?.toString() || '',
            check_in_date: (b.check_in_date || (b.check_in || '').split('T')[0]),
            check_out_date: (b.check_out_date || (b.check_out || '').split('T')[0]),
            booked_on_date: (b.booked_on_date || b.created_at || '').split('T')[0],
            send_whatsapp: b.send_whatsapp || false,
            whatsapp_number: b.whatsapp_number || '',
          });
        } else {
          toast({ title: 'Error', description: 'Booking not found', variant: 'destructive' });
          navigate(-1);
        }
      } catch (error) {
        console.error('Error fetching booking:', error);
        toast({ title: 'Error', description: 'Failed to load booking', variant: 'destructive' });
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    fetchBooking();
  }, [bookingId, navigate, toast]);

  // Calculate pending amount
  const pendingAmount = parseFloat(formData.total_amount || 0) - parseFloat(formData.advance_amount || 0);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleRoomChange = (index, field, value) => {
    const newRooms = [...formData.rooms];
    newRooms[index] = { ...newRooms[index], [field]: value };
    setFormData(prev => ({ ...prev, rooms: newRooms }));
    if (errors.rooms) {
      setErrors(prev => ({ ...prev, rooms: null }));
    }
  };

  const addRoom = () => {
    setFormData(prev => ({
      ...prev,
      rooms: [...prev.rooms, { room_name: '', room_count: 1 }]
    }));
  };

  const removeRoom = (index) => {
    if (formData.rooms.length > 1) {
      const newRooms = formData.rooms.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, rooms: newRooms }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.guest_name || formData.guest_name.trim().length < 2) {
      newErrors.guest_name = 'Guest name must be at least 2 characters';
    }
    
    const cleanMobile = formData.mobile_number.replace(/\D/g, '');
    if (cleanMobile.length !== 10) {
      newErrors.mobile_number = 'Mobile number must be exactly 10 digits';
    }
    
    const hasValidRoom = formData.rooms.some(r => r.room_name && r.room_name.trim() && r.room_count >= 1);
    if (!hasValidRoom) {
      newErrors.rooms = 'At least one room with a name is required';
    }
    
    const totalAmount = parseFloat(formData.total_amount);
    if (!totalAmount || totalAmount <= 0) {
      newErrors.total_amount = 'Total amount must be greater than 0';
    }
    
    const advanceAmount = parseFloat(formData.advance_amount || 0);
    if (advanceAmount > totalAmount) {
      newErrors.advance_amount = 'Advance cannot exceed total amount';
    }
    
    if (new Date(formData.check_out_date) < new Date(formData.check_in_date)) {
      newErrors.check_out_date = 'Check-out must be on or after check-in date';
    }
    
    if (!formData.booked_on_date) {
      newErrors.booked_on_date = 'Booked on date is required';
    }
    
    if (formData.send_whatsapp) {
      const cleanWhatsapp = (formData.whatsapp_number || '').replace(/\D/g, '');
      if (cleanWhatsapp.length !== 10) {
        newErrors.whatsapp_number = 'WhatsApp number must be exactly 10 digits';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Validation Error',
        description: 'Please fix the errors before saving',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        guest_name: formData.guest_name.trim(),
        mobile_number: formData.mobile_number.replace(/\D/g, ''),
        rooms: formData.rooms.filter(r => r.room_name && r.room_name.trim()).map(r => ({
          room_name: r.room_name.trim(),
          room_count: parseInt(r.room_count) || 1
        })),
        payment_mode: formData.payment_mode,
        advance_amount: parseFloat(formData.advance_amount) || 0,
        total_amount: parseFloat(formData.total_amount),
        check_in_date: formData.check_in_date,
        check_out_date: formData.check_out_date,
        booked_on_date: formData.booked_on_date,
        send_whatsapp: formData.send_whatsapp,
        whatsapp_number: formData.send_whatsapp ? formData.whatsapp_number.replace(/\D/g, '') : null,
      };

      const response = await bookingAPI.updateBooking(bookingId, updateData);
      
      if (response.STATUS === 1) {
        toast({ title: 'Success', description: 'Booking updated successfully!' });
        navigate(-1);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.detail || error?.message || 'Failed to update booking',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#1B2A7B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-32" data-testid="booking-edit-screen">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-navy shadow-md px-4 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-navy-light text-white hover:bg-navy-dark transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-white">Edit Booking</h1>
            <p className="text-xs text-gray-300">{booking?.booking_id}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Status Badge */}
        {booking && (
          <div className={`flex items-center gap-2 p-3 rounded-lg ${
            booking.booking_status === 'cancelled' ? 'bg-red-50 border border-red-200' :
            booking.is_checked_out ? 'bg-gray-50 border border-gray-200' :
            booking.is_checked_in ? 'bg-blue-50 border border-blue-200' :
            'bg-[#EEF1FC] border border-[#1B2A7B]/30'
          }`}>
            <AlertCircle className={`w-4 h-4 ${
              booking.booking_status === 'cancelled' ? 'text-red-500' :
              booking.is_checked_out ? 'text-gray-500' :
              booking.is_checked_in ? 'text-blue-500' :
              'text-[#1B2A7B]'
            }`} />
            <span className={`text-sm font-medium ${
              booking.booking_status === 'cancelled' ? 'text-red-700' :
              booking.is_checked_out ? 'text-gray-700' :
              booking.is_checked_in ? 'text-blue-700' :
              'text-[#1B2A7B]'
            }`}>
              Status: {booking.booking_status === 'cancelled' ? 'Cancelled' :
                booking.is_checked_out ? 'Checked Out' :
                booking.is_checked_in ? 'Checked In' : 'Confirmed'}
            </span>
          </div>
        )}

        {/* Guest Info Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <User className="w-4 h-4 text-[#1B2A7B]" />
            Guest Information
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Guest Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.guest_name}
                onChange={(e) => handleChange('guest_name', e.target.value)}
                className={`w-full px-4 py-3 bg-gray-50 rounded-lg border ${errors.guest_name ? 'border-red-400' : 'border-gray-200'} text-gray-900 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                data-testid="guest-name-input"
              />
              {errors.guest_name && <p className="text-red-500 text-xs mt-1">{errors.guest_name}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="tel"
                  value={formData.mobile_number}
                  onChange={(e) => handleChange('mobile_number', e.target.value)}
                  maxLength={10}
                  className={`w-full pl-12 pr-4 py-3 bg-gray-50 rounded-lg border ${errors.mobile_number ? 'border-red-400' : 'border-gray-200'} text-gray-900 focus:border-[#1B2A7B] focus:ring-1 focus:ring-emerald transition-colors`}
                  data-testid="mobile-input"
                />
              </div>
              {errors.mobile_number && <p className="text-red-500 text-xs mt-1">{errors.mobile_number}</p>}
            </div>
          </div>
        </div>

        {/* Rooms Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Home className="w-4 h-4 text-[#1B2A7B]" />
              Rooms
            </h3>
            <button onClick={addRoom} className="flex items-center gap-1 text-[#1B2A7B] text-sm font-medium" data-testid="add-room-btn">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          {errors.rooms && <p className="text-red-500 text-xs mb-3">{errors.rooms}</p>}
          <div className="space-y-3">
            {formData.rooms.map((room, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <input
                  type="text"
                  value={room.room_name}
                  onChange={(e) => handleRoomChange(index, 'room_name', e.target.value)}
                  placeholder="Room name"
                  className="flex-1 px-3 py-2 bg-white rounded-lg border border-gray-200 text-sm focus:border-[#1B2A7B]"
                  data-testid={`room-name-${index}`}
                />
                <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-2">
                  <button onClick={() => handleRoomChange(index, 'room_count', Math.max(1, room.room_count - 1))} className="p-1">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-6 text-center">{room.room_count}</span>
                  <button onClick={() => handleRoomChange(index, 'room_count', room.room_count + 1)} className="p-1">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {formData.rooms.length > 1 && (
                  <button onClick={() => removeRoom(index)} className="p-2 text-red-400 hover:text-red-600">
                    <Minus className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Payment Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#1B2A7B]" />
            Payment Details
          </h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleChange('payment_mode', 'UPI')}
                className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 ${
                  formData.payment_mode === 'UPI' ? 'bg-[#EEF1FC] border-[#1B2A7B] text-[#1B2A7B]' : 'border-gray-200'
                }`}
                data-testid="payment-upi"
              >
                <CreditCard className="w-5 h-5" /> UPI
              </button>
              <button
                onClick={() => handleChange('payment_mode', 'Cash')}
                className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 ${
                  formData.payment_mode === 'Cash' ? 'bg-[#EEF1FC] border-[#1B2A7B] text-[#1B2A7B]' : 'border-gray-200'
                }`}
                data-testid="payment-cash"
              >
                <Banknote className="w-5 h-5" /> Cash
              </button>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Total Amount *</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={formData.total_amount}
                  onChange={(e) => handleChange('total_amount', e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 bg-gray-50 rounded-lg border ${errors.total_amount ? 'border-red-400' : 'border-gray-200'} focus:border-[#1B2A7B]`}
                  data-testid="total-amount-input"
                />
              </div>
              {errors.total_amount && <p className="text-red-500 text-xs mt-1">{errors.total_amount}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Advance Amount</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                <input
                  type="number"
                  value={formData.advance_amount}
                  onChange={(e) => handleChange('advance_amount', e.target.value)}
                  className={`w-full pl-10 pr-4 py-3 bg-gray-50 rounded-lg border ${errors.advance_amount ? 'border-red-400' : 'border-gray-200'} focus:border-[#1B2A7B]`}
                  data-testid="advance-amount-input"
                />
              </div>
              {errors.advance_amount && <p className="text-red-500 text-xs mt-1">{errors.advance_amount}</p>}
            </div>

            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-orange-700">Pending Amount</span>
                <span className="text-lg font-bold text-orange-700">₹{pendingAmount > 0 ? pendingAmount.toLocaleString() : 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Dates Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#1B2A7B]" />
            Booking Dates
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">
                <CalendarPlus className="w-4 h-4" /> Booked On *
              </label>
              <input
                type="date"
                value={formData.booked_on_date}
                onChange={(e) => handleChange('booked_on_date', e.target.value)}
                className={`w-full px-3 py-3 bg-gray-50 rounded-lg border ${errors.booked_on_date ? 'border-red-400' : 'border-gray-200'} focus:border-[#1B2A7B]`}
                data-testid="booked-on-input"
              />
              {errors.booked_on_date && <p className="text-red-500 text-xs mt-1">{errors.booked_on_date}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Check-in</label>
                <input
                  type="date"
                  value={formData.check_in_date}
                  onChange={(e) => handleChange('check_in_date', e.target.value)}
                  className="w-full px-3 py-3 bg-gray-50 rounded-lg border border-gray-200 focus:border-[#1B2A7B]"
                  data-testid="check-in-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Check-out</label>
                <input
                  type="date"
                  value={formData.check_out_date}
                  onChange={(e) => handleChange('check_out_date', e.target.value)}
                  className={`w-full px-3 py-3 bg-gray-50 rounded-lg border ${errors.check_out_date ? 'border-red-400' : 'border-gray-200'} focus:border-[#1B2A7B]`}
                  data-testid="check-out-input"
                />
                {errors.check_out_date && <p className="text-red-500 text-xs mt-1">{errors.check_out_date}</p>}
              </div>
            </div>
          </div>
        </div>

        {/* WhatsApp Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#1B2A7B]" />
            WhatsApp Notification
          </h3>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.send_whatsapp}
                onChange={(e) => handleChange('send_whatsapp', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-[#1B2A7B] focus:ring-emerald"
                data-testid="send-whatsapp-checkbox"
              />
              <span className="text-sm text-gray-700">Send WhatsApp Notification</span>
            </label>

            {formData.send_whatsapp && (
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">WhatsApp Number *</label>
                <div className="relative">
                  <MessageCircle className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <div className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-500">+91</div>
                  <input
                    type="tel"
                    value={formData.whatsapp_number}
                    onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                    maxLength={10}
                    className={`w-full pl-[4.5rem] pr-4 py-3 bg-gray-50 rounded-lg border ${errors.whatsapp_number ? 'border-red-400' : 'border-gray-200'} focus:border-[#1B2A7B]`}
                    data-testid="whatsapp-number-input"
                  />
                </div>
                {errors.whatsapp_number && <p className="text-red-500 text-xs mt-1">{errors.whatsapp_number}</p>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg">
        <div className="max-w-lg mx-auto">
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="w-full py-4 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-50 flex items-center justify-center gap-2 transition-colors shadow-[#1B2A7B]/30"
            data-testid="save-booking-button"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BookingEditScreen;
